import { useState } from 'react';
import * as _ from 'lodash';
import { TextField, Button, Box, Flex, IconButton, ColorPicker, Text, Icon, Label, Dropdown, Checkbox, Divider } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';

import { Bold, Italic, Underline, Textcolor, HighlightColorBucket, Column } from '@vibe/icons';

import { BoardColumnConfig, BoardField } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';

const FONT_SIZE_OPTIONS = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '14',
    value: 14,
  },
  {
    label: '16',
    value: 16,
  },
  {
    label: '24',
    value: 24,
  },
  {
    label: '36',
    value: 36,
  },
];

const FONT_TYPE_OPTIONS = [
  {
    label: 'Times New Roman',
    value: 'Times New Roman',
  },
  {
    label: 'Arial',
    value: 'Arial',
  },
  {
    label: 'Tahoma',
    value: 'Tahoma',
  },
  {
    label: 'Verdana',
    value: 'Verdana',
  },
];

const HORIZONTAL_ALIGNMENT_OPTIONS: { label: string; value: 'left' | 'center' | 'right' }[] = [
  {
    label: 'Left',
    value: 'left',
  },
  {
    label: 'Center',
    value: 'center',
  },
  {
    label: 'Right',
    value: 'right',
  },
];

type ColumnConfigModalProps = {
  show: boolean;
  columnName: string;
  fieldName: string;
  onClose: (newConfig: BoardColumnConfig | null) => void;
  config: BoardColumnConfig | null;
  field: BoardField;
};

export function ColumnConfigModal({ show, columnName, fieldName, onClose, field, config: initialConfig }: ColumnConfigModalProps) {
  const [config, setConfig] = useState<BoardColumnConfig | null>(initialConfig ? _.cloneDeep(initialConfig) : {});
  const [showTextColor, setTextColor] = useState(false);
  const [showBackgroundColor, setBackgroundColor] = useState(false);

  const selectedFontSizeOption = FONT_SIZE_OPTIONS.find(({ value }) => value === config?.cell_font_size);
  const selectedFontType = FONT_TYPE_OPTIONS.find(({ value }) => value === config?.cell_font_type);
  const selectedHorizontalAlignment = HORIZONTAL_ALIGNMENT_OPTIONS.find(({ value }) => value === config?.cell_horizontal_alignment);

  return (
    <AppModal data-testid="" show={show} onClose={() => onClose(null)} width="default" alertDialog={false}>
      <AppModalContent>
        <Flex gap={8}>
          <Icon icon={Column} size={20} />
          <div style={{ maxWidth: '200px' }}>
            <Text type="text1" weight="medium" ellipsis>
              {fieldName}
            </Text>
          </div>
          <Label text={`Column ${columnName}`} color="dark" />
        </Flex>
        <Box marginTop="medium" border={true} borderColor="layoutBorderColor" rounded="small">
          <Box padding="medium" backgroundColor="greyBackgroundColor">
            <Box marginBottom="small">
              <Text type="text2" weight="medium">
                Column name
              </Text>
            </Box>
            <TextField
              placeholder="e.g. Budget"
              size="medium"
              onChange={(newHeadline) => {
                setConfig({
                  ...config,
                  custom_headline: newHeadline || '',
                });
              }}
              value={config?.custom_headline || ''}
            />
          </Box>
          {field.board_field_type === 'numbers' ? (
            <>
              <Divider withoutMargin />
              <Box padding="medium">
                <Box marginBottom="small">
                  <Text type="text2" weight="medium">
                    Transformers
                  </Text>
                </Box>
                <Checkbox
                  label="Convert Number to Text"
                  checked={config?.transformer === 'number_to_text'}
                  onChange={(ev) => {
                    setConfig({
                      ...config,
                      transformer: ev.target.checked ? 'number_to_text' : undefined,
                    });
                  }}
                />
              </Box>
            </>
          ) : (
            <></>
          )}
          <Divider withoutMargin />
          <Box padding="medium">
            <Box marginBottom="small">
              <Text type="text2" weight="medium">
                Font settings
              </Text>
            </Box>
            <Flex gap="small">
              <div style={{ flex: 1 }}>
                <Flex gap="small">
                  <div style={{ width: '65%' }}>
                    <Dropdown
                      searchable
                      options={FONT_TYPE_OPTIONS}
                      value={dropdownValue(selectedFontType)}
                      placeholder="Font"
                      clearable={false}
                      onChange={(newFontType) => {
                        setConfig({
                          ...config,
                          cell_font_type: newFontType.value,
                        });
                      }}
                    />
                  </div>
                  <div style={{ width: '35%' }}>
                    <Dropdown
                      searchable
                      options={FONT_SIZE_OPTIONS}
                      value={dropdownValue(selectedFontSizeOption)}
                      placeholder="Size"
                      clearable={false}
                      onChange={(newSize) => {
                        setConfig({
                          ...config,
                          cell_font_size: newSize.value,
                        });
                      }}
                    />
                  </div>
                </Flex>
              </div>
              <IconButton
                icon={Bold}
                kind="secondary"
                aria-label="Bold"
                active={config?.cell_font_bold}
                onClick={() => {
                  setConfig({
                    ...config,
                    cell_font_bold: !config?.cell_font_bold,
                  });
                }}
              />
              <IconButton
                icon={Italic}
                kind="secondary"
                aria-label="Italic"
                active={config?.cell_font_italic}
                onClick={() => {
                  setConfig({
                    ...config,
                    cell_font_italic: !config?.cell_font_italic,
                  });
                }}
              />
              <IconButton
                icon={Underline}
                kind="secondary"
                aria-label="Underline"
                active={config?.cell_font_underline}
                onClick={() => {
                  setConfig({
                    ...config,
                    cell_font_underline: !config?.cell_font_underline,
                  });
                }}
              />
              <IconButton
                icon={Textcolor}
                kind="secondary"
                aria-label="Text color"
                onClick={() => {
                  showTextColor ? setTextColor(false) : setTextColor(true);
                }}
                active={showTextColor}
              />
            </Flex>
            {showTextColor ? (
              <ColorPicker
                colorSize="small"
                numberOfColorsInLine={12}
                noColorText="Clear color"
                value={config?.cell_font_color}
                onSave={(val) => {
                  setConfig({
                    ...config,
                    cell_font_color: val ? val[0] : undefined,
                  });
                }}
              />
            ) : (
              <></>
            )}
          </Box>
          <Divider withoutMargin />
          <Box padding="medium">
            <Box marginBottom="small">
              <Text type="text2" weight="medium">
                Cell settings
              </Text>
            </Box>
            <Flex gap="small">
              <div style={{ flex: 1 }}>
                <Flex gap="small">
                  <div style={{ width: '100%' }}>
                    <Dropdown
                      searchable
                      options={HORIZONTAL_ALIGNMENT_OPTIONS}
                      value={dropdownValue(selectedHorizontalAlignment)}
                      placeholder="Alignment"
                      clearable={false}
                      onChange={(alignment) => {
                        setConfig({
                          ...config,
                          cell_horizontal_alignment: alignment.value,
                        });
                      }}
                    />
                  </div>
                </Flex>
              </div>
              <IconButton
                icon={HighlightColorBucket}
                kind="secondary"
                aria-label="Background color"
                onClick={() => {
                  showBackgroundColor ? setBackgroundColor(false) : setBackgroundColor(true);
                }}
                active={showBackgroundColor}
              />
            </Flex>
            {showBackgroundColor ? (
              <ColorPicker
                value={config?.cell_bg_color}
                colorSize="small"
                numberOfColorsInLine={12}
                noColorText="Clear color"
                onSave={(val) => {
                  setConfig({
                    ...config,
                    cell_bg_color: val ? val[0] : undefined,
                  });
                }}
              />
            ) : (
              <></>
            )}
          </Box>
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              <Button
                kind="tertiary"
                onClick={() => {
                  onClose(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onClose(config);
                }}
              >
                Confirm
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </AppModal>
  );
}
